import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Download, Upload, Database, HardDrive, AlertTriangle, Shield, FileArchive,
  Loader2, CheckCircle2, Rocket, Calendar, Trash2, Eye, RotateCcw, Clock, Sparkles, X, Building2,
} from "lucide-react";
import { toast } from "sonner";

function formatSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function formatDateFr(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function TechnicalAdmin() {
  const [snapshot, setSnapshot] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [backups, setBackups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [restoring, setRestoring] = useState(false);

  const loadAll = async () => {
    try {
      const [snap, ob, bks, se] = await Promise.all([
        api.get("/admin/site-snapshot"),
        api.get("/admin/onboarding/status").catch(() => ({ data: null })),
        api.get("/admin/backups").catch(() => ({ data: [] })),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      setSnapshot(snap.data);
      setOnboardingStatus(ob.data);
      setBackups(bks.data || []);
      setSettings(se.data);
    } catch (e) {
      toast.error("Accès refusé — rôle Agence requis");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const toggleOnboarding = async (v) => {
    setToggling(true);
    try {
      await api.put("/admin/onboarding/toggle", { enabled: v });
      const { data } = await api.get("/admin/onboarding/status");
      setOnboardingStatus(data);
      toast.success(`Mode Onboarding ${v ? "activé — le client verra l'assistant" : "désactivé"}`);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setToggling(false); }
  };

  const saveBackupSettings = async (patch) => {
    try {
      const { data } = await api.put("/settings", { ...settings, ...patch });
      setSettings(data);
      toast.success("Préférences de sauvegarde enregistrées");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const createNow = async () => {
    setCreating(true);
    try {
      await api.post("/admin/backups/create-now");
      toast.success("Sauvegarde créée");
      loadAll();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  };

  const doPreview = async (bid) => {
    setPreviewingId(bid);
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/backups/${bid}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
      setPreviewingId(null);
    }
  };

  const doRestore = async () => {
    if (!preview) return;
    if (restoreMode === "replace" && !window.confirm("⚠️ Mode REPLACE destructif : toutes les données actuelles seront remplacées. Continuer ?")) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/admin/backups/${previewingId}/restore`, { mode: restoreMode });
      toast.success(`Restauration terminée (${Object.values(data.restored).reduce((a, b) => a + b, 0)} documents)`);
      setPreviewingId(null);
      setPreview(null);
      loadAll();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setRestoring(false); }
  };

  const doDelete = async (bid) => {
    if (!window.confirm("Supprimer cette sauvegarde ?")) return;
    try {
      await api.delete(`/admin/backups/${bid}`);
      toast.success("Supprimée");
      loadAll();
    } catch (e) { toast.error("Erreur"); }
  };

  const doDownload = (bid) => {
    const a = document.createElement("a");
    a.href = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${bid}/download`;
    a.download = "";
    // include token via same-origin fetch instead
    api.get(`/admin/backups/${bid}/download`).then(r => {
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `site-backup-${bid.slice(0, 8)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Téléchargement impossible"));
  };

  if (!snapshot) {
    return (
      <div className="p-10">
        <div className="max-w-md mx-auto text-center">
          <Shield className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-2" />
          <div className="font-semibold text-[color:var(--brand-navy)]">Zone réservée Agence</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Cet onglet nécessite un rôle Admin (Agence).</div>
        </div>
      </div>
    );
  }

  const totalDocs = Object.values(snapshot.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="technical-admin">
      <header>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--brand-sky)] mb-1">
          <Shield className="w-3.5 h-3.5" />Technique <Badge className="bg-[color:var(--brand-navy)] text-white text-[9px] uppercase tracking-wider border-0">Agence</Badge>
        </div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Sauvegardes & outils système</h1>
      </header>

      {/* Site snapshot */}
      <Card className="p-5 space-y-3" data-testid="site-snapshot">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Database className="w-4 h-4" />État du site</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-[color:var(--brand-sky)]/5 border border-[color:var(--brand-sky)]/20">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Documents totaux</div>
            <div className="text-2xl font-bold text-[color:var(--brand-navy)]">{totalDocs.toLocaleString("fr-FR")}</div>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700">Taille base</div>
            <div className="text-2xl font-bold text-emerald-700">{snapshot.data_size_mb ?? "—"} <span className="text-sm">MB</span></div>
          </div>
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="text-[10px] uppercase tracking-wider text-amber-700">Version backend</div>
            <div className="text-2xl font-bold text-amber-700">{snapshot.version}</div>
          </div>
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-700">Sauvegardes</div>
            <div className="text-2xl font-bold text-slate-700">{backups.length}<span className="text-xs ml-1 text-slate-500">/30</span></div>
          </div>
        </div>
      </Card>

      {/* Accès Agence mode toggle */}
      {settings && (
        <Card className="p-5 space-y-3" data-testid="hub-mode-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-[color:var(--brand-navy)]">Mode Accès Agence</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] max-w-xl mt-1">
                  À n'activer que sur <strong>l'instance Emergent dédiée à l'Accès Agence</strong> (pas sur les sites clients).
                  Active les onglets "Mes sites clients" (dashboard multi-tenant) et "Alertes" (monitoring proactif).
                  Le receiver <code>/api/agency/heartbeat</code> fonctionne dans les deux cas, mais l'UI n'est visible qu'ici.
                </div>
                {!!settings.is_hub && (
                  <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                    ✓ Cette instance est configurée comme Accès Agence — menu "Mes sites clients" disponible.
                  </div>
                )}
              </div>
            </div>
            <Switch
              checked={!!settings.is_hub}
              onCheckedChange={async (v) => {
                try {
                  await api.put("/settings", { is_hub: v });
                  setSettings({ ...settings, is_hub: v });
                  toast.success(v ? "Mode Accès Agence activé — rechargez la page pour voir le menu mis à jour" : "Mode Accès Agence désactivé");
                } catch (e) { toast.error("Erreur"); }
              }}
              data-testid="hub-mode-toggle"
            />
          </div>
        </Card>
      )}

      {/* Onboarding toggle */}
      {onboardingStatus && (
        <Card className="p-5 space-y-3" data-testid="onboarding-toggle-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white flex items-center justify-center">
                <Rocket className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-[color:var(--brand-navy)]">Mode Onboarding</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] max-w-xl mt-1">
                  Active un assistant guidé 7 étapes côté client. L'agence reçoit un email à 100%.
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span>Progression : <strong>{onboardingStatus.percent}%</strong></span>
                  {onboardingStatus.completed_at && <span className="text-emerald-700">✓ Complété {formatDateFr(onboardingStatus.completed_at)}</span>}
                </div>
              </div>
            </div>
            <Switch checked={!!onboardingStatus.enabled} onCheckedChange={toggleOnboarding} disabled={toggling} data-testid="onboarding-toggle" />
          </div>
        </Card>
      )}

      {/* Backup scheduler settings */}
      <Card className="p-5 space-y-3" data-testid="backup-scheduler-card">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Clock className="w-4 h-4" />Sauvegarde automatique</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              Une sauvegarde complète est créée chaque jour. Les sauvegardes de plus de <strong>30 jours</strong> sont automatiquement supprimées.
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs">Heure de sauvegarde (UTC)</Label>
              <Select
                value={String(settings?.auto_backup_hour ?? 3)}
                onValueChange={v => saveBackupSettings({ auto_backup_hour: +v })}
              >
                <SelectTrigger className="w-24 h-9" data-testid="auto-backup-hour"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 22, 23, 0].map(h => <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Switch
              checked={settings?.auto_backup_enabled !== false}
              onCheckedChange={v => saveBackupSettings({ auto_backup_enabled: v })}
              data-testid="auto-backup-toggle"
            />
          </div>
        </div>
      </Card>

      {/* Backup history */}
      <Card className="p-5 space-y-3" data-testid="backups-history">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Calendar className="w-4 h-4" />Historique des sauvegardes</div>
          <Button onClick={createNow} disabled={creating} className="bg-[color:var(--brand-navy)]" size="sm" data-testid="create-backup-now">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileArchive className="w-3.5 h-3.5 mr-1.5" />}
            Sauvegarder maintenant
          </Button>
        </div>

        {backups.length === 0 ? (
          <div className="p-8 text-center border-dashed border-2 rounded-lg text-sm text-[hsl(var(--muted-foreground))]">
            Aucune sauvegarde pour le moment. La première sera créée ce soir.
          </div>
        ) : (
          <div className="space-y-1.5">
            {backups.map(b => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))]/60 transition" data-testid={`backup-row-${b.id.slice(0, 8)}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${b.auto ? "bg-[color:var(--brand-sky)]" : "bg-emerald-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--brand-navy)]">
                    {formatDateFr(b.created_at)}
                    {!b.auto && <Badge variant="outline" className="text-[9px] uppercase tracking-wider">Manuelle</Badge>}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    {b.doc_count} docs · {formatSize(b.size_bytes)} · #{b.id.slice(0, 8)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => doPreview(b.id)} className="h-8" title="Aperçu" data-testid={`preview-backup-${b.id.slice(0, 8)}`}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => doDownload(b.id)} className="h-8" title="Télécharger">
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => doDelete(b.id)} className="h-8 text-red-600" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Preview modal */}
      {previewingId && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { setPreviewingId(null); setPreview(null); }}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={e => e.stopPropagation()} data-testid="backup-preview-modal">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Eye className="w-4 h-4" />Aperçu de la sauvegarde</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Révisez avant de restaurer.</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setPreviewingId(null); setPreview(null); }}><X className="w-4 h-4" /></Button>
            </div>
            {!preview ? (
              <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-[color:var(--brand-sky)]/10 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--brand-sky)]">Date</div>
                    <div className="text-sm font-bold text-[color:var(--brand-navy)] mt-1">{formatDateFr(preview.created_at)}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Documents</div>
                    <div className="text-sm font-bold text-emerald-700 mt-1">{preview.doc_count}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">Taille</div>
                    <div className="text-sm font-bold text-amber-700 mt-1">{formatSize(preview.size_bytes)}</div>
                  </div>
                </div>

                {/* Site snapshot visual */}
                {preview.site_snapshot && (
                  <div className="p-3 rounded-lg bg-white border" data-testid="preview-site-snapshot">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">Site à ce moment-là</div>
                    <div className="flex items-center gap-3">
                      {preview.site_snapshot.logo_url && <img src={preview.site_snapshot.logo_url} alt="" className="w-12 h-12 rounded object-contain bg-white border" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-[color:var(--brand-navy)] truncate">{preview.site_snapshot.company_name || "—"}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{preview.site_snapshot.tagline || preview.site_snapshot.meta_title || ""}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Diff table */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">Différences avec l'état actuel</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
                    {Object.entries(preview.diff || {}).map(([col, d]) => {
                      const hasDelta = d.delta !== 0;
                      return (
                        <div key={col} className={`flex justify-between px-2.5 py-1.5 rounded font-mono ${hasDelta ? "bg-amber-50" : "bg-[hsl(var(--muted))]/40"}`}>
                          <span className="text-[color:var(--brand-navy)]">{col}</span>
                          <span className={`font-bold ${hasDelta ? "text-amber-700" : "text-[hsl(var(--muted-foreground))]"}`}>
                            {d.backup} ← {d.current}{hasDelta ? ` (${d.delta > 0 ? "+" : ""}${d.delta})` : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Restore controls */}
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-center gap-3">
                    <Label className="text-xs">Mode :</Label>
                    <Select value={restoreMode} onValueChange={setRestoreMode}>
                      <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="merge">Merge (recommandé)</SelectItem>
                        <SelectItem value="replace">Replace (destructif)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => { setPreviewingId(null); setPreview(null); }}>Fermer</Button>
                    <Button onClick={doRestore} disabled={restoring} className="bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)]" data-testid="restore-backup-btn">
                      {restoring ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
                      Rétablir cette version
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      <div className="text-xs text-[hsl(var(--muted-foreground))] text-center py-2 flex items-center justify-center gap-1.5">
        <HardDrive className="w-3 h-3" />Les sauvegardes sont stockées dans votre base de données. Pensez à télécharger un backup manuel avant toute opération sensible.
      </div>
    </div>
  );
}
